import { equals, pick } from 'ramda'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { RoutesMap } from 'redux-first-router'

import { FlagValues } from '~shared-components/components/FlagRender/useFlags'
import { usePrevious } from '~shared-components/hooks/usePrevious'
import { syncRoutesMapAction } from '~shared-components/redux/actions/location.actions'
import { SupportedCountry } from '~shared-components/utils/enums'

const flattenFeatureFlagsPerCountry = (
  country: SupportedCountry,
  featureFlags: FlagValues,
): Record<keyof FlagValues, boolean> =>
  Object.entries(featureFlags).reduce(
    (acc, [key, value]) =>
      key.includes('PerCountry')
        ? {
            ...acc,
            [key]: ((value as unknown) as Record<SupportedCountry, boolean>)[
              country
            ],
          }
        : { ...acc, [key]: value },
    {},
  )

export const useSyncRoutes = (
  country: SupportedCountry,
  featureFlags: FlagValues,
  routeDependencies: RoutesMap,
) => {
  const prevFlags = usePrevious(featureFlags)
  const dispatch = useDispatch()

  let flatFlags: Record<keyof FlagValues, boolean>
  let flatPrevFlags: Record<keyof FlagValues, boolean> | undefined
  const flagsNotArrived = Object.values(featureFlags).every(
    flag => flag === undefined,
  )
  let flagsNotEqual = false
  if (country && !flagsNotArrived) {
    const pickKeys = Object.values(routeDependencies) as string[]
    flatFlags = pick(
      pickKeys,
      flattenFeatureFlagsPerCountry(country, featureFlags),
    )
    flatPrevFlags =
      prevFlags &&
      pick(pickKeys, flattenFeatureFlagsPerCountry(country, prevFlags))
    flagsNotEqual =
      flatPrevFlags === undefined || !equals(flatFlags, flatPrevFlags)
  }

  useEffect(() => {
    if (country && !flagsNotArrived) {
      dispatch(syncRoutesMapAction(routeDependencies, flatFlags))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagsNotEqual, country, flagsNotArrived])
}
